/**
 * formal-exam.ts
 *
 * Formal mock-exam UI.
 *
 * Design rules:
 *  - Serif font (Microsoft JhengHei + Noto Serif TC), exam-paper feel
 *  - One question per page, NO going back (each answer is locked on "下一題")
 *  - NO correct/wrong feedback during the exam
 *  - Timer: 30 min (midterm) / 50 min (final)
 *  - End screen: score X/100 + list of missed KPs
 */

import { ExamController } from '../exam';
import { mountEscapeButton } from '../escape-button';
import { loadProgress, saveProgress, createFreshProgress, addFactionXP } from '../../shared/progress';
import { FACTIONS, factionForSubject } from '../../shared/factions';
import type { Subject } from '../../shared/types';
import type { GeneratedQuestion } from '../generators/math/types';
import { composeMockPaper } from './mock-paper-composer';
import type { MockPaperOpts } from './mock-paper-composer';
import { renderExamResultSummary } from './exam-result-summary';
import type { PaperResult, PaperPerQuestion } from './paper-view';
import { friendlyKPName } from '../data/kp-display-names';

// ─── Constants ───────────────────────────────────────────────────────────────

const TIMER_SEC: Record<'midterm' | 'final', number> = {
  midterm: 30 * 60,
  final:   50 * 60,
};

const SUBJECT_LABELS: Record<Subject, string> = {
  math:    '數學',
  chinese: '國語',
  english: '英語',
  nature:  '自然',
  social:  '社會',
};

// ─── Entry point ─────────────────────────────────────────────────────────────

export interface FormalExamOpts {
  container: HTMLElement;
  subject: Subject;
  level: 'midterm' | 'final';
  rngSeed?: number;
  onDone: () => void;
}

export async function runFormalExam(opts: FormalExamOpts): Promise<void> {
  const { container, subject, level, rngSeed, onDone } = opts;

  // Loading screen
  const loadingEl = document.createElement('div');
  loadingEl.className = 'formal-exam-loading';
  loadingEl.textContent = '正在組卷，請稍候…';
  container.appendChild(loadingEl);

  let questions: GeneratedQuestion[];
  try {
    const paperOpts: MockPaperOpts = { subject, level, rngSeed };
    questions = await composeMockPaper(paperOpts);
  } catch (e) {
    loadingEl.textContent = `組卷失敗：${String(e)}`;
    const retryBtn = document.createElement('button');
    retryBtn.className = 'primary-btn';
    retryBtn.style.marginTop = '16px';
    retryBtn.textContent = '返回';
    retryBtn.addEventListener('click', () => { loadingEl.remove(); onDone(); });
    loadingEl.appendChild(retryBtn);
    return;
  }
  loadingEl.remove();

  if (questions.length === 0) {
    const err = document.createElement('div');
    err.className = 'formal-exam-loading';
    err.innerHTML = `<p>目前沒有可用的題目（${SUBJECT_LABELS[subject]}的 ready KP 為空）。</p>`;
    const back = document.createElement('button');
    back.className = 'primary-btn';
    back.textContent = '返回';
    back.addEventListener('click', () => { err.remove(); onDone(); });
    err.appendChild(back);
    container.appendChild(err);
    return;
  }

  const timeLimitSec = TIMER_SEC[level];
  const exam = new ExamController(questions, timeLimitSec, 'formal');

  // ── Build root DOM ───────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'formal-exam';
  container.appendChild(root);

  // Header bar
  const header = document.createElement('div');
  header.className = 'fe-header';
  root.appendChild(header);

  const titleEl = document.createElement('span');
  titleEl.className = 'fe-title';
  titleEl.textContent = `${SUBJECT_LABELS[subject]} ${level === 'midterm' ? '期中考' : '期末考'} 模擬卷`;
  header.appendChild(titleEl);

  const posEl = document.createElement('span');
  posEl.className = 'fe-position';
  header.appendChild(posEl);

  const timerEl = document.createElement('span');
  timerEl.className = 'fe-timer';
  header.appendChild(timerEl);

  // Question card
  const card = document.createElement('div');
  card.className = 'fe-card';
  root.appendChild(card);

  const questionNumEl = document.createElement('div');
  questionNumEl.className = 'fe-question-num';
  card.appendChild(questionNumEl);

  const promptEl = document.createElement('div');
  promptEl.className = 'fe-prompt';
  card.appendChild(promptEl);

  const answerArea = document.createElement('div');
  answerArea.className = 'fe-answer-area';
  card.appendChild(answerArea);

  const nextBtn = document.createElement('button');
  nextBtn.className = 'fe-next-btn';
  nextBtn.textContent = '下一題 →';
  nextBtn.disabled = true;
  card.appendChild(nextBtn);

  // ── Timer ────────────────────────────────────────────────────────────────

  let timerInterval: ReturnType<typeof setInterval> | null = null;

  function formatTime(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = Math.ceil(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  function tickTimer(): void {
    const rem = exam.remainingSec();
    timerEl.textContent = `⏱ ${formatTime(rem)}`;
    if (rem <= 60) timerEl.classList.add('fe-timer--urgent');
    if (exam.isTimedOut()) {
      stopTimer();
      finish();
    }
  }

  function startTimer(): void {
    tickTimer();
    timerInterval = setInterval(tickTimer, 1000);
  }

  function stopTimer(): void {
    if (timerInterval !== null) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  }

  // ── Render question ───────────────────────────────────────────────────────

  let currentAnswer: number | string | null = null;

  function renderQuestion(): void {
    currentAnswer = null;
    nextBtn.disabled = true;
    answerArea.innerHTML = '';

    const q = exam.currentQuestion();
    const pos = exam.position();

    posEl.textContent = `第 ${pos.current} 題 / 共 ${pos.total} 題`;
    questionNumEl.textContent = `第 ${pos.current} 題`;
    promptEl.textContent = q.prompt;

    if (q.kind === 'mcq') {
      q.options.forEach((opt, idx) => {
        const btn = document.createElement('button');
        btn.className = 'fe-choice';
        btn.textContent = `${String.fromCharCode(65 + idx)}. ${opt}`;
        btn.dataset.idx = String(idx);
        btn.addEventListener('click', () => {
          // Deselect others
          answerArea.querySelectorAll('.fe-choice').forEach(b => b.classList.remove('fe-choice--selected'));
          btn.classList.add('fe-choice--selected');
          currentAnswer = idx;
          nextBtn.disabled = false;
        });
        answerArea.appendChild(btn);
      });
    } else {
      // fill
      const input = document.createElement('input');
      input.className = 'fe-fill-input';
      input.type = 'text';
      input.placeholder = '請輸入答案';
      input.autocomplete = 'off';
      input.addEventListener('input', () => {
        currentAnswer = input.value.trim() || null;
        nextBtn.disabled = !currentAnswer;
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !nextBtn.disabled) nextBtn.click();
      });
      answerArea.appendChild(input);
      // Focus after render
      requestAnimationFrame(() => input.focus());
    }
  }

  // ── Next-button handler (locks answer) ───────────────────────────────────

  nextBtn.addEventListener('click', () => {
    if (currentAnswer === null) return;
    if (exam.done) return;

    // Disable interaction
    nextBtn.disabled = true;
    answerArea.querySelectorAll<HTMLElement>('button, input').forEach(el => {
      if (el instanceof HTMLButtonElement) el.disabled = true;
      if (el instanceof HTMLInputElement) el.readOnly = true;
    });

    exam.submit(currentAnswer as number | string);

    if (exam.done) {
      stopTimer();
      finish();
    } else {
      renderQuestion();
    }
  });

  // ── Escape / 放棄 ─────────────────────────────────────────────────────────

  const unmountEscape = mountEscapeButton(() => {
    stopTimer();
    unmountEscape();
    root.remove();
    onDone();
  });

  // ── Finish screen ─────────────────────────────────────────────────────────

  function finish(): void {
    unmountEscape();
    stopTimer();

    const result = exam.finalize();
    const scorePercent = Math.round((result.correctCount / result.totalQuestions) * 100);

    // Persist XP
    let progress = loadProgress();
    if (!progress) progress = createFreshProgress('指揮官');
    const faction = factionForSubject(subject);
    addFactionXP(progress, faction, Math.round(result.totalXP));
    saveProgress(progress);

    const missedKPs = new Set<string>();
    for (const pq of result.perQuestion) {
      if (!pq.correct) missedKPs.add(pq.question.kp);
    }

    const factionInfo = FACTIONS[faction];

    // Build the PaperResult shape that renderExamResultSummary expects, so
    // mock-exam (task #59) and formal-exam share spec §4.4 chapter-breakdown.
    const perQuestion: PaperPerQuestion[] = result.perQuestion.map(pq => ({
      question: pq.question,
      answer: pq.answer,
      correct: pq.correct,
      skipped: pq.answer === null,
    }));
    const answeredCount = perQuestion.filter(pq => !pq.skipped).length;
    const elapsedSec = (Date.now() - exam.startedAtMs) / 1000;
    const paperResult: PaperResult = {
      correctCount: result.correctCount,
      totalQuestions: result.totalQuestions,
      answeredCount,
      totalXP: Math.round(result.totalXP),
      elapsedSec,
      perQuestion,
    };

    root.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'fe-result';
    wrap.innerHTML = `
      <h1 class="fe-result-title">考試結束</h1>
      <div class="fe-score-ring">
        <span class="fe-score-num">${scorePercent}</span>
        <span class="fe-score-label">分</span>
      </div>
      <p class="fe-xp-earned">獲得 ${Math.round(result.totalXP)} ${factionInfo.emoji} ${factionInfo.displayName} 修煉值</p>
    `;
    root.appendChild(wrap);

    // Chapter-breakdown summary (spec §4.4) — no raw KP id appears.
    renderExamResultSummary(root, paperResult, subject, 0, () => {
      root.remove();
      onDone();
    });

    // Friendly per-KP missed list (spec §6 friendlyKPName precedent). Kept
    // separate from the chapter breakdown so parents see exact lesson paths
    // for the KPs to revisit — e.g. 「數學 › 第 2 章 › 二位數乘一位數」.
    if (missedKPs.size > 0) {
      const missedEl = document.createElement('div');
      missedEl.className = 'fe-missed-kps';
      missedEl.innerHTML = `
        <h3>需要加強的知識點：</h3>
        <ul class="fe-missed-kp-list">${
          [...missedKPs].map(k => `<li class="fe-missed-kp">${friendlyKPName(k)}</li>`).join('')
        }</ul>
      `;
      root.appendChild(missedEl);
    }
  }

  // ── Start ─────────────────────────────────────────────────────────────────

  renderQuestion();
  startTimer();
}
